import { DocumentTextIcon, HashtagIcon, LightningBoltIcon, ReceiptTaxIcon, TagIcon } from "@heroicons/react/outline";
import { PencilIcon } from "@heroicons/react/solid";
import { useOne, useParse } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import Button from "components/button";
import { DescriptionItem } from "components/descriptionItem";
import { FullScreenLoading } from "components/fullScreenLoading";
import { IHomepageCouponDetail, SupportedLanguage } from "interfaces";
import { useState } from "react";
import { Link } from "react-router-dom";
import { CONTENT_LANGUAGES } from "utills/constants";
import { get } from "lodash";
import { HistoryLogButton } from "components/historyLogButton";
import { isHaveActionPermission } from "utills/helpers";

export const HomepageCouponDetail: React.FC = () => {
  const [selectedContentLanguage, setSelectedContentLanguage] = useState(SupportedLanguage.EN);
  const [selectedTitleLanguage, setSelectedTitleLanguage] = useState(SupportedLanguage.EN);

  const parse = useParse();
  const { params } = parse();
  const {
    data,
    isFetching,
    isPreviousData,
    isLoading,
  } = useOne<IHomepageCouponDetail>({
    resource: "mkt/coupon-homepages",
    id: params?.id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!params?.id,
    } as any,
  });

  const homepageCouponDetail = data?.data ?? null;

  if (isLoading) {
    return <FullScreenLoading />
  }

  return (
    <div className="w-full flex flex-col items-center gap-y-3 sm:gap-y-4">
      <Breadcrumbs
        customPageLabel="Homepage coupon Detail"
        additionalLabel={
          <div className="flex gap-x-2.5">
            <HistoryLogButton
              resource={`mkt/coupon-homepages/${homepageCouponDetail?.id}/history`}
              className="w-11 h-11 sm:w-auto"
            />
            {isHaveActionPermission("homepage_coupon", "mkt", "edit") ? (
              <Link
                to={`/mkt/homepage-coupon/${homepageCouponDetail?.id}/edit`}
              >
                <Button className="w-11 h-11 sm:w-auto">
                  <span className="sr-only">Edit</span>
                  <PencilIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">Edit</span>
                </Button>
              </Link>
            ) : (
              <></>
            )}
          </div>
        }
      />
      <div className="box-container p-0">
        <div className="divide-y divide-gray-100">
          <DescriptionItem
            icon={TagIcon}
            label="Status"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {homepageCouponDetail?.is_active ? "Active" : "Inactive"}
              </p>
            }
          />
          <DescriptionItem
            icon={ReceiptTaxIcon}
            label="Coupon code"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {homepageCouponDetail?.coupon_code}
              </p>
            }
          />
          <DescriptionItem
            icon={LightningBoltIcon}
            label="Soldout"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {homepageCouponDetail?.is_sold_out ? "Yes" : "No"}
              </p>
            }
          />
          <DescriptionItem
            icon={HashtagIcon}
            label="Position"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {homepageCouponDetail?.position}
              </p>
            }
          />
          <DescriptionItem
            icon={DocumentTextIcon}
            label="Title"
            additionalLabel={(
              <div className="flex flex-row gap-x-1.5 p-1.5 w-fit rounded-full outline outline-solid outline-[1px] outline-brand-500">
                {CONTENT_LANGUAGES.map((lang, index) => (
                  <img
                    key={lang.value}
                    src={`/images/flags/circle-${lang.value}.png`}
                    alt="Language Icon"
                    className={`w-4 shrink-0 cursor-pointer ${lang.value !== selectedTitleLanguage ? 'opacity-20' : ''}`}
                    onClick={() => setSelectedTitleLanguage(lang.value)}
                  />
                ))}
              </div>
            )}
            content={
              <div>
                <p className="whitespace-pre-line text-sm leading-6">{get(homepageCouponDetail, `title_${selectedTitleLanguage}`, "")}</p>
              </div>
            }
          />
          <DescriptionItem
            icon={DocumentTextIcon}
            label="Content"
            additionalLabel={(
              <div className="flex flex-row gap-x-1.5 p-1.5 w-fit rounded-full outline outline-solid outline-[1px] outline-brand-500">
                {CONTENT_LANGUAGES.map((lang, index) => (
                  <img
                    key={lang.value}
                    src={`/images/flags/circle-${lang.value}.png`}
                    alt="Language Icon"
                    className={`w-4 shrink-0 cursor-pointer ${lang.value !== selectedContentLanguage ? 'opacity-20' : ''}`}
                    onClick={() => setSelectedContentLanguage(lang.value)}
                  />
                ))}
              </div>
            )}
            content={
              <div>
                <p className="whitespace-pre-line text-sm leading-6">{get(homepageCouponDetail, `content_${selectedContentLanguage}`, "")}</p>
              </div>
            }
          />
        </div>
      </div>
      {(isFetching && isPreviousData) ? <FullScreenLoading /> : <></>}
    </div>
  );
}